import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Canvas from '~/shared/components/Canvas';
import MoneySavingInfo from '~/components/MoneySavingInfo';
import {Card, CardSelectButton} from '@customink/pigment-react';

import './SideToDrop.scss';

export function getOriginalItemPrice({itemizedPrices, productId}) {
  if (!productId || !itemizedPrices?.toJS()) {
    return null;
  }
  const itemizedPricesObj = itemizedPrices.toJS();
  const itemizedPricesForProduct = itemizedPricesObj[productId];
  return itemizedPricesForProduct?.itemPrice;
}

export function getDiscountItemPrice({
  itemizedPricesWithoutDroppedView,
  productViewName,
  productId
}) {
  if (
    !productViewName ||
    !productId ||
    !itemizedPricesWithoutDroppedView?.toJS()
  ) {
    return null;
  }
  const itemizedPricesWDP = itemizedPricesWithoutDroppedView.toJS();
  const priceAfterDroppedView = itemizedPricesWDP[productViewName];

  if (!priceAfterDroppedView?.itemizedPrices) {
    return null;
  }

  const priceAfterDroppedViewForProduct =
    priceAfterDroppedView.itemizedPrices[productId];

  return priceAfterDroppedViewForProduct?.itemPrice;
}

/** @param {PropTypes.InferProps<typeof propTypes>} props - Component properties. */
export const SideToDrop = props => {
  const {
    hideIcons,
    selectedProduct,
    selectedProductColor,
    productView,
    viewConstraint,
    icons,
    itemizedPrices,
    itemizedPricesWithoutDroppedView,
    isSelected,
    minimumConstraintDim,
    onClickCallback
  } = props;

  const [originalPrice, setOriginalPrice] = useState(null);
  const [discountPrice, setDiscountPrice] = useState(null);

  useEffect(() => {
    function setComparisonOriginalDiscountPrices({
      selectedProductColor,
      productView,
      itemizedPrices,
      itemizedPricesWithoutDroppedView
    }) {
      if (
        !selectedProductColor ||
        !productView ||
        !itemizedPrices ||
        !itemizedPricesWithoutDroppedView
      ) {
        return;
      }

      const productId = selectedProductColor.id;
      const productViewName = productView.name;
      const originalItemPrice = getOriginalItemPrice({
        itemizedPrices,
        productId
      });
      const discountItemPrice = getDiscountItemPrice({
        itemizedPricesWithoutDroppedView,
        productViewName,
        productId
      });

      if (!originalItemPrice || !discountItemPrice) {
        return;
      }

      setOriginalPrice(originalItemPrice);
      setDiscountPrice(discountItemPrice);
    }

    setComparisonOriginalDiscountPrices({
      selectedProductColor,
      productView,
      itemizedPrices,
      itemizedPricesWithoutDroppedView
    });
  }, [
    selectedProductColor,
    productView,
    itemizedPrices,
    itemizedPricesWithoutDroppedView
  ]);

  function render() {
    if (
      !selectedProduct ||
      !selectedProductColor ||
      !productView ||
      !viewConstraint ||
      !icons ||
      !minimumConstraintDim
    ) {
      return <></>;
    }

    return (
      <Card
        selected={isSelected}
        data-testid={`side-${productView.name}`}
        className="ndx-SideToDrop"
        onClick={() => {
          if (productView?.id && onClickCallback) {
            onClickCallback({
              productViewId: productView?.id
            });
          }
        }}>
        <CardSelectButton
          selected={isSelected}
          sx={{display: isSelected ? undefined : 'none'}}
        />
        <Canvas
          constraint={viewConstraint}
          currentColorId={selectedProductColor.id}
          guidelines={productView.guidelines}
          icons={hideIcons ? [] : icons}
          minimumConstraintHeight={minimumConstraintDim}
          minimumConstraintWidth={minimumConstraintDim}
          product={selectedProduct}
          productColor={selectedProductColor}
          selectedView={productView}
        />
        <MoneySavingInfo
          originalPrice={originalPrice}
          discountPrice={discountPrice}
        />
      </Card>
    );
  }

  return render();
};

const propTypes = {
  hideIcons: PropTypes.bool,
  icons: PropTypes.arrayOf(PropTypes.object),
  isSelected: PropTypes.bool,
  itemizedPrices: PropTypes.object,
  itemizedPricesWithoutDroppedView: PropTypes.object,
  minimumConstraintDim: PropTypes.number,
  productView: PropTypes.object,
  selectedProduct: PropTypes.object,
  selectedProductColor: PropTypes.object,
  viewConstraint: PropTypes.object,
  onClickCallback: PropTypes.func
};

SideToDrop.propTypes = propTypes;

export default SideToDrop;
